import { ViewType } from 'common/constants';
import { urlWithTenant } from 'common/utils/utilFunctions';

import {
	IAIRecipesResponse,
	IGetAISharedSimulation,
	SimulationData,
} from 'features/AiSimulation/api/type-def';
import {
	useState,
	useEffect,
	useSelector,
	Grid,
	useNavigate,
	Typography,
	RootState,
	styles,
	Loader,
	AICard,
	Chip,
	Stack,
	AI_TYPE,
	toSentenceCase,
	getAiRecipePath,
	dateInCorrectFormatWithFullYear,
	AlertComponent,
	AIShareTable,
	AIsharedResponse,
	HTTP_STATUS_CODES,
	setToastMessage,
	useDispatch,
	getAISharedSimulationData,
	APP_GENERIC_BAD_REQUEST_MSG,
	APP_GENERIC_DATA_NOT_FOUND_MSG,
	APP_GENERAL_ERROR_MSG,
	NO_DATA_FOUND,
	useCallback,
	getAISimulationModelsData,
	getAISimulationRecipesData,
	setAIModelDetail,
	IAIModelDetails,
	getRoutePathForModel,
	setAIRecipeDetail,
	IAIRecipeDetails,
	useTableQuery,
} from 'features/AiSimulation/SimulationArena/SimulationArenaModules';

const SharedSimulations = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [aiSharedSimulationApiData, setAISharedSimulationApiData] =
		useState<AIsharedResponse[]>();
	const [isSharedLoading, setIsSharedLoading] = useState<boolean>(false);
	const [aiSharedTotalRecordsCount, setAISharedTotalRecordCount] =
		useState<number>(0);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const { SHARED, RECIPE } = AI_TYPE;
	const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const simulationViewType = useSelector(
		(state: RootState) => state.simulationArena.simulationViewTypeValue,
	);

	const [viewType, setViewType] = useState<ViewType>(simulationViewType);

	const { tableQueryObj: shareTableQueryObj } = useTableQuery({
		id: 'simulation_share_table',
	});

	useEffect(() => {
		setViewType(simulationViewType);
	}, [simulationViewType]);

	const displayToast = (message: string, severity: string) => {
		dispatch(
			setToastMessage({
				message,
				severity,
				isToastOpen: true,
			}),
		);
	};

	const handleErrorResponse = (responseFromApi: any) => {
		if (responseFromApi?.statusCode === BAD_REQUEST) {
			displayToast(APP_GENERIC_BAD_REQUEST_MSG, 'error');
		} else if (responseFromApi?.statusCode === NOT_FOUND) {
			displayToast(APP_GENERIC_DATA_NOT_FOUND_MSG, 'error');
		} else {
			displayToast(APP_GENERAL_ERROR_MSG, 'error');
		}
	};

	useEffect(() => {
		fetchAISharedSimulationResponse(
			loggedInUser?.userId || '',
			shareTableQueryObj,
		);
	}, [simulationViewType]);

	const fetchAISharedSimulationResponse = async (
		userData: string,
		data: IGetAISharedSimulation,
	) => {
		setIsSharedLoading(true);

		const responseFromApi = await getAISharedSimulationData(userData, data);

		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			setAISharedSimulationApiData(
				responseFromApi?.responseBody.sharedSimulations,
			);
			setAISharedTotalRecordCount(responseFromApi?.responseBody?.totalCount);
			setIsSharedLoading(false);
		} else {
			handleErrorResponse(responseFromApi);
			setIsSharedLoading(false);
		}
	};

	const handleSharedPagination = (data: IGetAISharedSimulation) => {
		fetchAISharedSimulationResponse(loggedInUser?.userId || '', data);
	};

	const fetchAIRecipesResponse = async (data: IAIRecipesResponse) => {
		const responseFromApi = await getAISimulationRecipesData(data);
		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			const recipeData = responseFromApi?.responseBody
				?.data[0] as IAIRecipeDetails;
			dispatch(
				setAIRecipeDetail({
					...recipeData,
					isShared: true,
				}),
			);
			navigate(urlWithTenant(getAiRecipePath(recipeData.models, true)));
		} else {
			handleErrorResponse(responseFromApi);
		}
	};

	const fetchAIModelsResponse = async (data: any) => {
		const responseFromApi = await getAISimulationModelsData(data);
		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			dispatch(
				setAIModelDetail(
					responseFromApi?.responseBody?.data[0] as IAIModelDetails,
				),
			);
			navigate(
				urlWithTenant(
					getRoutePathForModel(
						responseFromApi?.responseBody?.data[0]?.workflowStep,
						true,
					),
				),
			);
		} else {
			handleErrorResponse(responseFromApi);
		}
	};

	const onClickShareSimulation = async (sharedData: SimulationData) => {
		const data = {
			ids: sharedData.simulationId,
		};
		if (sharedData?.type?.toLowerCase() === RECIPE) {
			fetchAIRecipesResponse(data);
		} else {
			fetchAIModelsResponse(data);
		}
	};

	return (
		<div>
			{viewType === ViewType.GRID ? (
				<Grid
					className={`${styles['section-content']} ${styles['grid-style']}`}
					container
					spacing={3}
				>
					{isSharedLoading && (
						<Grid item sm={12}>
							<Loader
								show={isSharedLoading}
								loaderText="Loading..."
								className={styles['loader-alignment']}
							/>
						</Grid>
					)}
					{!isSharedLoading &&
						(aiSharedSimulationApiData !== undefined &&
						aiSharedSimulationApiData.length > 0 ? (
							aiSharedSimulationApiData?.map((res: AIsharedResponse) => (
								<Grid
									item
									key={res.id}
									xs={12}
									sm={12}
									md={4}
									lg={4}
									xl={4}
									direction="row"
								>
									<AICard
										data={res}
										type={SHARED}
										title={res.simulationName}
										onClickHandler={onClickShareSimulation}
										key={res.id}
									>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center"
											className={styles['aicard-content']}
										>
											<Chip
												key={res.type}
												label={res.type}
												className={styles['type-style']}
												variant="outlined"
											/>
											<Typography className={styles['date-style']}>
												{toSentenceCase('Last Updated')}:{' '}
												{dateInCorrectFormatWithFullYear(res.sharedDate)}
											</Typography>
										</Stack>
									</AICard>
								</Grid>
							))
						) : (
							<Grid item xs={12} className={`${styles['ai-card-no-content']}`}>
								<Typography>{toSentenceCase(NO_DATA_FOUND)}</Typography>
							</Grid>
						))}
				</Grid>
			) : (
				<AIShareTable
					navigateToSharedSimulation={onClickShareSimulation}
					totalItems={aiSharedTotalRecordsCount}
					handlePagination={handleSharedPagination}
					rowData={aiSharedSimulationApiData || []}
					isLoading={isSharedLoading}
				/>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</div>
	);
};

export default SharedSimulations;
